export interface GroundTruthPlanItem {
    task: string;
    task_id?: string;
    sub_query: string;
}

export interface GroundTruthVizItem {
    chart_type: string;
    chartTitle: string;
    [key: string]: unknown;
}

export interface GroundTruthNarrative {
    overview?: string;
    key_takeaways?: string[];
    assumptions?: string[];
    formulae?: string[];
}

export interface GroundTruthHITLValidation {
    should_use_value?: unknown;
    should_complete?: boolean;
    [key: string]: unknown;
}

export interface GroundTruthHITLConfig {
    expected?: boolean;
    scripted_response?: string;
    scripted_responses?: string[];
    validation?: GroundTruthHITLValidation;
    [key: string]: unknown;
}

export type GroundTruthHITL = Record<string, GroundTruthHITLConfig>;

export interface GroundTruthPerTaskItem {
    task: string;
    task_id?: string;
    sub_query?: string;
    sql_queries?: string[];
    sql_db_output?: TaskResultSqlOutput[];
    viz?: GroundTruthVizItem | GroundTruthVizItem[];
    narrative?: GroundTruthNarrative;
}

export interface GroundTruth {
    GroundTruthIntent?: string;
    GroundTruthRefine?: string;
    GroundTruthTasks?: string[];
    GroundTruthPlan?: GroundTruthPlanItem[];
    GroundTruthNarrative?: GroundTruthNarrative;
    GroundTruthHITL?: GroundTruthHITL;
    GroundTruthViz?: GroundTruthVizItem[];
    GroundTruthPerTask?: GroundTruthPerTaskItem[];
}

export interface TaskResultSqlOutput {
    columns: string[];
    rows: Record<string, unknown>[];
}

export interface TaskResult {
    task: string;
    task_name: string;
    success: boolean;
    sql_db_output?: TaskResultSqlOutput[];
}

export interface HitlTraceEntry {
    run_id?: string;
    agent?: string;
    type?: string;
    content?: string;
    user_response?: string | null;
    response_run_id?: string | null;
    [key: string]: unknown;
}

export interface SourceTurnMetadata {
    thread_id?: string;
    selected_run_id?: string;
    source_run_ids?: string[];
    canonical_question?: string;
    completed?: boolean;
    [key: string]: unknown;
}

export interface GroundTruthMetadata {
    source_thread_id?: string;
    source_run_id?: string;
    source_run_ids?: string[];
    source_turn?: SourceTurnMetadata;
    hitl_trace?: HitlTraceEntry[];
    task_results?: TaskResult[];
    available_fixtures?: EvalFixtureSummary[];
    selected_fixture?: EvalSelectedFixtureMetadata;
    fixture_missing?: boolean;
    target_agent?: string | null;
    last_regeneration?: EvalRegenerationMetadata | null;
    last_regeneration_preview?: EvalRegenerationMetadata | null;
    [key: string]: unknown;
}

export type EvalFixtureStatus = 'available' | 'stale';

export interface EvalStaleFixtureRef {
    fixture_id?: string | number;
    id?: string | number;
    logical_agent?: string;
    agent_name?: string;
    task_id?: string | null;
    reason?: string;
    stale_reason?: string | null;
    [key: string]: unknown;
}

export interface EvalFixtureSummary {
    id?: string | number;
    fixture_id?: string | number;
    agent_name?: string;
    logical_agent?: string;
    task_id?: string | null;
    source_node?: string | null;
    capture_stage?: string | null;
    status?: EvalFixtureStatus;
    stale_reason?: string | null;
    [key: string]: unknown;
}

export interface EvalFixtureRow {
    id?: string | number;
    fixture_id?: string | number;
    agent_name: string;
    fixture: unknown;
    status?: EvalFixtureStatus;
    stale_reason?: string | null;
    metadata?: {
        logical_agent?: string;
        task_id?: string | null;
        status?: EvalFixtureStatus;
        stale_reason?: string | null;
        [key: string]: unknown;
    } | null;
    source_node?: string | null;
    capture_stage?: string | null;
    [key: string]: unknown;
}

export interface EvalSelectedFixtureMetadata {
    id?: string | number;
    fixture_id?: string | number;
    agent_name?: string;
    logical_agent?: string;
    task_id?: string | null;
    source_node?: string | null;
    capture_stage?: string | null;
}

export interface EvalRegenerationStageResult {
    stage?: string;
    task_id?: string | null;
    status?: string;
    reason?: string;
    query_count?: number;
    [key: string]: unknown;
}

export interface EvalRegenerationMetadata {
    action?: string;
    regenerated_fields?: string[];
    stale_fields?: string[];
    stale_fixtures?: EvalStaleFixtureRef[];
    stage_results?: EvalRegenerationStageResult[];
    prompt_branch?: string | null;
    domains_dir?: string | null;
    regenerated_at?: string;
    [key: string]: unknown;
}

export interface EvalGroundTruthFixturesRefreshRequest {
    fixture_ids: Array<string | number>;
    dry_run?: boolean;
}

export interface EvalGroundTruthFixtureRefreshResult {
    fixture_id?: string | number;
    id?: string | number;
    status?: string;
    reason?: string;
    logical_agent?: string;
    agent_name?: string;
    [key: string]: unknown;
}

export interface EvalGroundTruthFixturesRefreshResponse {
    results?: EvalGroundTruthFixtureRefreshResult[];
    stale_fixtures_remaining?: EvalStaleFixtureRef[] | Array<string | number>;
    [key: string]: unknown;
}

export type GroundTruthStatus = 'draft' | 'reviewed' | 'approved';

export interface GroundTruthComment {
    author: string;
    text: string;
    created_at: string;
}

export interface CommentCreateRequest {
    text: string;
}

export interface EvalGroundTruthRequest {
    question_set: string;
    question: string;
    target_agent?: string | null;
    ground_truth?: GroundTruth;
    fixture?: unknown;
    hitl_test?: GroundTruthHITL | null;
    metadata?: GroundTruthMetadata;
    initial_comment?: CommentCreateRequest | null;
    status?: GroundTruthStatus;
}

export type FixtureCoverageStatus = 'full' | 'partial' | 'none' | 'stale';

export interface FixtureCoverage {
    status: FixtureCoverageStatus;
    [key: string]: unknown;
}

export interface EvalGroundTruthResponse extends EvalGroundTruthRequest {
    id: string;
    comments?: GroundTruthComment[] | null;
    version: number;
    created_by?: string;
    updated_by?: string;
    created_at?: string;
    updated_at?: string;
    fixture_coverage?: FixtureCoverage | null;
}

export interface EvalGroundTruthUpdateRequest {
    question_set?: string;
    question?: string;
    target_agent?: string | null;
    ground_truth?: Partial<GroundTruth>;
    fixture?: unknown;
    hitl_test?: GroundTruthHITL | null;
    metadata?: Partial<GroundTruthMetadata>;
    comments?: GroundTruthComment[] | null;
    status?: GroundTruthStatus;
}

export interface QuestionSetInfo {
    question_set: string;
    count: number;
}

export interface EvalGroundTruthHistoryEntry extends EvalGroundTruthResponse {
    change_type: 'created' | 'updated' | 'deleted';
    changed_by?: string;
    changed_at?: string;
}

export interface EvalListParams {
    question_set?: string;
    status?: GroundTruthStatus;
    offset?: number;
    limit?: number;
}

export interface EvalExportParams {
    question_set?: string;
    status?: GroundTruthStatus;
}

/** Draft response from the from-chat endpoint (same shape as EvalGroundTruthRequest + metadata) */
export interface EvalFromChatDraft {
    question_set: string;
    question: string;
    target_agent?: string | null;
    ground_truth: GroundTruth;
    fixture?: unknown;
    fixtures?: EvalFixtureRow[];
    hitl_test?: GroundTruthHITL | null;
    metadata: GroundTruthMetadata;
    comments?: GroundTruthComment[] | null;
    status: GroundTruthStatus;
}

export interface EvalGroundTruthFromChatOptions {
    question_set?: string;
    target_agent?: string;
    include_fixtures?: boolean;
}

export type EvalRegenerationTargetAgent = 'intent' | 'planner' | 'refine' | 'narrative' | 'sql' | 'task_narrative' | 'visualization';

export interface EvalGroundTruthRegenerateRequest {
    target_agent: EvalRegenerationTargetAgent;
    task_id?: string | null;
    fixture_id?: string | number | null;
    prompt_branch?: string | null;
    domains_dir?: string | null;
    dry_run?: boolean;
}

export interface EvalGroundTruthRefreshDataRequest {
    prompt_branch?: string | null;
    domains_dir?: string | null;
    dry_run?: boolean;
}

export interface EvalGroundTruthBulkRefreshDataRequest extends EvalGroundTruthRefreshDataRequest {
    entry_ids?: Array<string | number>;
    question_set?: string;
    target_agent?: string | null;
    status?: GroundTruthStatus;
}

export interface EvalGroundTruthBulkRegenerateRequest extends EvalGroundTruthRefreshDataRequest {
    target_agent: EvalRegenerationTargetAgent;
    entry_ids?: Array<string | number>;
    question_set?: string | null;
    status?: GroundTruthStatus | null;
    task_id?: string | null;
}

export interface EvalGroundTruthBulkRefreshResult {
    id: string | number;
    status: string;
    metadata?: EvalRegenerationMetadata;
    error?: string;
}

export interface EvalGroundTruthBulkRefreshResponse {
    matched: number;
    updated: number;
    errors: number;
    dry_run: boolean;
    results: EvalGroundTruthBulkRefreshResult[];
}

export type EvalGroundTruthBulkRegenerateResult = EvalGroundTruthBulkRefreshResult;
export type EvalGroundTruthBulkRegenerateResponse = EvalGroundTruthBulkRefreshResponse;

export const INTENT_OPTIONS = [
    { value: 'enterprise_data', label: 'Enterprise Data' },
    { value: 'history', label: 'History' },
    { value: 'learning', label: 'Learning' },
    { value: 'out_of_scope', label: 'Out of Scope' },
] as const;

export const STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'approved', label: 'Approved' },
] as const;

export const CHART_TYPE_OPTIONS = [
    { value: 'table', label: 'Table' },
    { value: 'Bar', label: 'Bar Chart' },
    { value: 'VerticalBar', label: 'Vertical Bar' },
    { value: 'Line', label: 'Line Chart' },
    { value: 'horizontalbar', label: 'Horizontal Bar' },
    { value: 'stackbar', label: 'Stacked Bar' },
    { value: 'waterfall', label: 'Waterfall' },
    { value: 'bpl', label: 'Brand Price Ladder' },
] as const;

// --- Eval Runs ---

export type EvalRunStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface EvalRunResponse {
    id: number;
    status: EvalRunStatus;
    config_name: string;
    config_snapshot: Record<string, unknown> | null;
    question_set: string | null;
    target_agent: string | null;
    question_count: number | null;
    output_dir: string | null;
    error_message: string | null;
    overview_metrics: Record<string, unknown> | null;
    triggered_by: string | null;
    started_at: string;
    completed_at: string | null;
    repeats: number | null;
}

export interface EvalAgentOutputs {
    per_task?: Record<string, unknown>;
    task_sql?: Record<string, unknown>;
    task_visualization?: Record<string, unknown>;
    task_narrative?: Record<string, unknown>;
    narrative_structured?: GroundTruthNarrative | Record<string, unknown>;
    hitl_validation?: Record<string, unknown>;
    [key: string]: unknown;
}

export interface EvalPerQuestionResult {
    question_index: number;
    question_set: string;
    query: string;
    intent: string | null;
    error: string | null;
    row_pass: boolean | null;
    ground_truth: Record<string, unknown>;
    agent_outputs?: EvalAgentOutputs;
    agent_evaluations?: Record<string, Record<string, Record<string, unknown>>>;
    gate_metrics?: Record<string, boolean>;
    combined_evaluations?: Record<string, Record<string, unknown>>;
}

export interface EvalRunResultsResponse {
    run: EvalRunResponse;
    overview_metrics: Record<string, unknown> | null;
    agents_evaluated: string[] | null;
    gate_results: Array<Record<string, unknown>> | null;
    per_question_results: EvalPerQuestionResult[] | null;
}

export interface TriggerEvalRunPayload {
    config_name?: string;
    question_set?: string;
    question_sets?: string[];
    target_agent?: string;
    question_ids?: number[];
    repeats?: number;
    tenant?: string;
    triggered_by?: string;
}

export interface EvalRunListParams {
    status?: EvalRunStatus;
    question_set?: string;
    target_agent?: string;
    offset?: number;
    limit?: number;
}

// --- Consistency metrics (returned when repeats > 1) ---

export interface ConsistencyFieldOutput {
    run_index: number;
    // May be a plain string, a JSON/NDJSON string, or an already-parsed object depending on the field.
    output: unknown;
}

export interface ConsistencyFieldDetail {
    exact_match_rate: number;
    unique_outputs: number;
    outputs_per_run: ConsistencyFieldOutput[];
}

export interface ConsistencyPerQuestion {
    group: string;
    query: string;
    runs: number;
    fields: Record<string, ConsistencyFieldDetail>;
}

export interface ConsistencyAggregate {
    mean_exact_match: number;
    min_exact_match: number;
}

export interface ConsistencyScoreStat {
    mean: number;
    min: number;
    count?: number;
}

export interface ConsistencyStructuredSection {
    per_question: Array<{ group: string; metrics: Record<string, ConsistencyScoreStat> }>;
    aggregate: Record<string, ConsistencyScoreStat>;
}

export interface ConsistencyPerTaskEntry {
    task: string;
    task_presence_consistent: boolean;
    runs_present: number;
    metrics: Record<string, ConsistencyScoreStat>;
}

export interface ConsistencyPerTaskSection {
    per_question: Array<{ group: string; tasks: ConsistencyPerTaskEntry[] }>;
    aggregate: Record<string, ConsistencyScoreStat>;
}

export interface ConsistencyMetrics {
    aggregate: Record<string, ConsistencyAggregate>;
    per_question: ConsistencyPerQuestion[];
    llm_available?: boolean;
    plan_structure?: ConsistencyStructuredSection;
    whole_response?: ConsistencyStructuredSection;
    per_task?: ConsistencyPerTaskSection;
}

// --- Dashboard types ---

export interface DashboardMetricPoint {
    runId: number;
    startedAt: string;
    metricKey: string;
    value: number;
}

export interface DashboardRunSummary {
    runId: number;
    startedAt: string;
    configName: string;
    questionSet: string | null;
    targetAgent: string | null;
    repeats: number | null;
    status: EvalRunStatus;
    durationMs: number | null;
    passRate: number | null;
    errorRate: number | null;
    questionCount: number | null;
    metrics: Record<string, number>;
    consistency: ConsistencyMetrics | null;
    tenant?: string;
    configSnapshot?: Record<string, unknown>;
}

export interface DashboardFilters {
    questionSet?: string;
    targetAgent?: string;
    configName?: string;
    tenant?: string;
    dateRange?: [string, string];
}

export interface PromptChangePoint {
    sha: string;
    author: string;
    date: string;
    message: string;
    formattedDate: string;
}

export interface PromptImpactRow {
    sha: string;
    author: string;
    date: string;
    message: string;
    avgPassRateBefore: number | null;
    avgPassRateAfter: number | null;
    avgSimilarityBefore: number | null;
    avgSimilarityAfter: number | null;
    runsBefore: number;
    runsAfter: number;
}

export interface DashboardInsight {
    type: 'success' | 'warning' | 'info' | 'error';
    message: string;
}

export const TARGET_AGENT_OPTIONS = [
    { value: '', label: 'Full pipeline' },
    { value: 'intent', label: 'Intent' },
    { value: 'refine', label: 'Refine' },
    { value: 'planner', label: 'Planner' },
    { value: 'narrative', label: 'Narrative' },
    { value: 'execution', label: 'Execution' },
    { value: 'sql', label: 'SQL' },
    { value: 'visualization', label: 'Visualization' },
    { value: 'task_narrative', label: 'Task Narrative' },
    { value: 'retrieval', label: 'Retrieval' },
] as const;

export const REGENERATION_TARGET_AGENT_OPTIONS = [
    { value: 'intent', label: 'Intent' },
    { value: 'refine', label: 'Refine' },
    { value: 'planner', label: 'Planner' },
    { value: 'narrative', label: 'Narrative' },
    { value: 'sql', label: 'SQL' },
    { value: 'visualization', label: 'Visualization' },
    { value: 'task_narrative', label: 'Task Narrative' },
] as const;
